/**
 * HTTP client for the admin console API.
 *
 * Auth is the Django session cookie, so every request must send credentials and
 * every unsafe request must carry the CSRF token Django set on that cookie.
 * There is no token refresh or Authorization header here by design — see
 * backend/api/admin_api/permissions.py.
 */

export const API_BASE = '/api/admin';

/** Thrown for any non-2xx response. Carries the parsed body when there is one. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** The session expired or the user was never signed in. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** Field-level validation errors from the autosave endpoint, if present. */
  get fieldErrors(): Record<string, string[]> {
    if (this.body && typeof this.body === 'object' && 'field_errors' in this.body) {
      const errors = (this.body as { field_errors?: unknown }).field_errors;
      if (errors && typeof errors === 'object') {
        return errors as Record<string, string[]>;
      }
    }
    return {};
  }
}

function readCookie(name: string): string {
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return '';
}

function csrfToken(): string {
  return readCookie('csrftoken');
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Appended as a query string. Array values become repeated keys. */
  params?: QueryParams;
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined | Array<string | number>
>;

export function buildQuery(params: QueryParams | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      // The Django filters accept comma-joined multi-values, which keeps URLs
      // short enough to stay readable and shareable.
      if (value.length) search.set(key, value.join(','));
      continue;
    }
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (UNSAFE_METHODS.has(method)) {
    headers['X-CSRFToken'] = csrfToken();
  }

  const url = `${API_BASE}${path}${buildQuery(options.params)}`;
  const response = await fetch(url, {
    method,
    headers,
    // Required: the session cookie is the credential.
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  const payload = await parseBody(response);

  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(response, payload), payload);
  }
  return payload as T;
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    // Django's login redirect and error pages return HTML; surface the status
    // rather than a wall of markup.
    return { detail: await response.text() };
  }
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function isReportable(value: unknown): value is string {
  return typeof value === 'string' && Boolean(value) && !value.trimStart().startsWith('<');
}

function errorMessage(response: Response, payload: unknown): string {
  // DRF serialises `ValidationError('some reason')` as a bare list, so reading
  // only object keys turned every such rejection into "Request failed (400)"
  // and hid the reason the user needed.
  if (Array.isArray(payload)) {
    const reasons = payload.filter(isReportable);
    if (reasons.length) {
      return reasons.join(' ');
    }
  }
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    for (const key of ['detail', 'message', 'error']) {
      if (isReportable(record[key])) {
        return record[key] as string;
      }
    }
    // Field-keyed errors — `{"statement": ["This field cannot be blank."]}` —
    // are the other shape DRF and Django forms produce.
    for (const value of Object.values(record)) {
      if (Array.isArray(value)) {
        const reasons = value.filter(isReportable);
        if (reasons.length) {
          return reasons.join(' ');
        }
      }
    }
  }
  if (response.status === 403) return 'You do not have permission to do that.';
  if (response.status === 404) return 'Not found.';
  if (response.status >= 500) return 'The server ran into a problem. Please try again.';
  return `Request failed (${response.status}).`;
}

/** Multipart POST to the console API (storage uploads). */
async function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    // Content-Type is intentionally unset so the browser adds the boundary.
    headers: { 'X-CSRFToken': csrfToken(), Accept: 'application/json' },
    credentials: 'same-origin',
    body: form,
  });

  const payload = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(response, payload), payload);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, params?: QueryParams, signal?: AbortSignal) =>
    apiRequest<T>(path, { params, signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) => apiRequest<T>(path, { method: 'POST', body, signal }),
  postForm: apiPostForm,
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/**
 * GET JSON from an absolute Django admin URL.
 *
 * User phone-number listing lives on the ModelAdmin, not under /api/admin, so
 * the client calls that URL directly with the session cookie.
 */
export async function getAdminJson<T>(url: string, params?: QueryParams): Promise<T> {
  const response = await fetch(`${url}${buildQuery(params)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  const payload = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(response, payload), payload);
  }
  return payload as T;
}

/**
 * POST to a Django admin action URL.
 *
 * The action endpoints (end-live, todos, fraud-flag, saved views) already exist
 * as JSON admin views, and the API embeds their absolute URLs in its payloads.
 * They accept form encoding, not JSON, so they need their own helper.
 */
export async function postAdminForm<T>(
  url: string,
  fields: Record<string, string | number | boolean> = {},
): Promise<T> {
  const body = new URLSearchParams();
  for (const [key, value] of Object.entries(fields)) {
    body.set(key, String(value));
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRFToken': csrfToken(),
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: body.toString(),
  });

  const payload = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(response, payload), payload);
  }
  return payload as T;
}

/**
 * POST JSON to an absolute Django URL outside the /api/admin prefix.
 *
 * The memory feedback views (parse, submit, transcribe) are pre-existing JSON
 * endpoints under /admin/memory/, so they are called directly rather than
 * proxied through a new API surface.
 *
 * They report failures as 200 with `{ok: false, error}`, so that shape is
 * promoted to an ApiError to keep error handling uniform with `api.*`.
 */
export async function postJson<T extends { ok?: boolean }>(
  url: string,
  body: unknown,
): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken(),
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });

  const payload = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(response, payload), payload);
  }

  const record = (payload ?? {}) as Record<string, unknown>;
  if (record.ok === false) {
    throw new ApiError(response.status, errorMessage(response, payload), payload);
  }
  return payload as T;
}

/** Multipart POST to an absolute Django URL (audio uploads). */
export async function postFormData<T>(url: string, form: FormData): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    // Content-Type is intentionally unset so the browser adds the boundary.
    headers: { 'X-CSRFToken': csrfToken(), Accept: 'application/json' },
    credentials: 'same-origin',
    body: form,
  });

  const payload = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, errorMessage(response, payload), payload);
  }
  return payload as T;
}
